import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { QueryClient } from "@tanstack/react-query";

import {
  assignSettingProductPageReactQuery,
  getSettingProductPageReactQuery,
} from "@faire/retailer/settings/getSettingProductPageReactQuery";
import { ProductCacheStore } from "@faire/retailer/stores/domain/ProductCacheStore";
import {
  buildProductImageGridSrcSet,
  IMAGE_GRID_MAX_IMAGES_VISIBLE,
} from "@faire/retailer/views/product/ProductImages/utils";
import { prefetchLeanProduct } from "@faire/retailer/views/product/utils/useLeanProductQuery";

import { PreloadedImageTracker } from "./PreloadedImageTracker";

interface ProductDetails {
  productToken: string | undefined;
  bestImage: IImage | undefined;
  images: Array<IImage>;
  showVerticalImagery?: boolean;
}

export function preloadNavigationToPDP(
  queryClient: QueryClient,
  product: ProductDetails
): void {
  assignSettingProductPageReactQuery();
  if (product.productToken) {
    if (getSettingProductPageReactQuery()) {
      prefetchLeanProduct(queryClient, product.productToken);
    } else {
      ProductCacheStore.get().fetchLeanProduct(product.productToken);
    }
  }

  PreloadedImageTracker.get().clear();

  const { Image } = getWindowOrThrow();

  const showVerticalImagery = product.showVerticalImagery || false;

  getUniqueImages(product).forEach((image) => {
    const { src, srcSet } = buildProductImageGridSrcSet(
      image,
      showVerticalImagery
    );
    if (!src || !srcSet) {
      return;
    }

    const img = new Image();
    img.srcset = srcSet;
    img.src = src;

    PreloadedImageTracker.get().setInfo(src, {
      img,
      showVerticalImagery,
    });
  });
}

function getUniqueImages(product: ProductDetails): IImage[] {
  const images = product.images ? [...product.images] : [];
  const bestImage = product.bestImage;

  if (bestImage?.url && images.every((image) => image.url !== bestImage.url)) {
    images.unshift(bestImage);
  }

  return images.slice(0, IMAGE_GRID_MAX_IMAGES_VISIBLE);
}
